import { useCallback, useEffect, useState } from 'react';
import {
  askChildRecord,
  summarizeAskConversation,
} from '../../services/askRecord/askRecordService';

const getAskConversationContext = (conversation) => conversation.slice(-8).map((turn) => ({
  answer: turn.answer,
  question: turn.question,
  sources: (turn.sources || [])
    .filter((source) => source.date)
    .map((source) => ({ date: source.date })),
}));

export const useAskRecord = ({ childId } = {}) => {
  const [conversation, setConversation] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);
  const [summaryError, setSummaryError] = useState('');
  const [summaryLoading, setSummaryLoading] = useState(false);

  const ask = useCallback(async (question) => {
    setLoading(true);
    setError('');

    try {
      const result = await askChildRecord({
        childId,
        question,
        conversation: getAskConversationContext(conversation),
      });
      setConversation((current) => [...current, { question, ...result }]);
      setSummary(null);
      setSummaryError('');
      return result;
    } catch (askError) {
      const message = askError?.message || 'We could not answer that right now.';
      setError(message);
      throw askError;
    } finally {
      setLoading(false);
    }
  }, [childId, conversation]);

  const createSummary = useCallback(async () => {
    setSummaryLoading(true);
    setSummaryError('');
    try {
      const result = await summarizeAskConversation({
        childId,
        turns: conversation.map((turn) => ({
          question: turn.question,
          sourceIds: (turn.sources || []).map((source) => source.id).filter(Boolean),
        })),
      });
      setSummary(result);
      return result;
    } catch (summaryRequestError) {
      setSummaryError(
        summaryRequestError?.message || 'We could not create that summary right now.'
      );
      throw summaryRequestError;
    } finally {
      setSummaryLoading(false);
    }
  }, [childId, conversation]);

  const reset = useCallback(() => {
    setConversation([]);
    setError('');
    setSummary(null);
    setSummaryError('');
    setSummaryLoading(false);
  }, []);

  useEffect(() => {
    reset();
  }, [childId, reset]);

  return {
    answer: conversation.at(-1)?.answer || null,
    ask,
    confidence: conversation.at(-1)?.confidence || 'low',
    conversation,
    createSummary,
    error,
    followUps: conversation.at(-1)?.followUps || [],
    loading,
    reset,
    sources: conversation.at(-1)?.sources || [],
    summary,
    summaryError,
    summaryLoading,
  };
};

export default useAskRecord;
